import React, { useState } from "react";
import { LabelledRow } from "components";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import { Button } from "semantic-ui-react";
import UnitOfDistanceDropdown from "components/dropdowns/reactselect/UnitOfDistanceDropdown";

interface ModifyExclusivityClauseModalProps {
  showId: number
  clause?: server.dto.ExclusivityClause
  onSaved: (newClause: server.dto.ExclusivityClause) => void
}

export const ModifyExclusivityClauseModal = (props: ModifyExclusivityClauseModalProps) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [enterPressed, setEnterPressed] = useState<boolean>(false);
  const [daysBefore, setDaysBefore] = useState<number>(props.clause?.daysBefore || 0);
  const [daysAfter, setDaysAfter] = useState<number>(props.clause?.daysAfter || 0);
  const [radius, setRadius] = useState<number>(props.clause?.radius || 0);
  const [unitOfDistance, setUnitOfDistance] = useState<server.dto.UnitsOfDistance>(props.clause?.unitOfDistance || "metric");

  const validators = useValidators();

  const onSubmit = (e?) => {
    e?.preventDefault();
    saveExclusivity();
  };

  const saveExclusivity = async () => {
    setIsSaving(true);

    const validationResult = await validators.validateAll();
    if (validationResult.hasErrors) {
      setIsSaving(false);
      return;
    }

    const exclusivityClauseDto: server.dto.SetExclusivityClauseInstruction = {
      showId: props.showId,
      daysAfter,
      daysBefore,
      radius,
      unitOfDistance
    }

    await window.CoreApi.Show.SetExclusivityClause(exclusivityClauseDto);

    setIsSaving(false);
    props.onSaved({
      daysAfter,
      daysBefore,
      radius,
      unitOfDistance,
    });
  }

  const onEnterPressed = () => {
    setEnterPressed(true);
  }

  // We use this workaround because first onblur the numeric input, and next render we submit the form
  React.useEffect(() => {
    if (enterPressed && !isSaving) {
      setEnterPressed(false);
      saveExclusivity();
    }
  }, [enterPressed]);

  return <>
    <div>
      <ValidatorContext settings={validators.validatorSettings}>
        <table className="s1_table bordergrid gray">
          <tbody>
            <LabelledRow label={RESX.Distance.Radius}>
              <div className="flex gap-small">
                <RequiredNumberInput
                  width={60}
                  validatorKey="radius"
                  value={radius}
                  onChange={v => setRadius(v)}
                  onEnterPressed={onEnterPressed}
                  decimalPoints={0}
                />
                <div style={{ width: 150 }}>
                  <UnitOfDistanceDropdown
                    value={unitOfDistance}
                    onChange={v => setUnitOfDistance(v)}
                  />
                </div>
              </div>
            </LabelledRow>
            <LabelledRow label={RESX.DateTime.Period}>
              <div className="flex-column gap-small">
                <div className="flex gap-small align-center">
                  <RequiredNumberInput
                    width={60}
                    validatorKey="daysBefore"
                    value={daysBefore}
                    onChange={v => setDaysBefore(v)}
                    onEnterPressed={onEnterPressed}
                    decimalPoints={0}
                  />
                  {RESX.Exclusivity.daysBeforeShow}
                </div>
                <div className="flex gap-small align-center">
                  <RequiredNumberInput
                    width={60}
                    validatorKey="daysAfter"
                    value={daysAfter}
                    onChange={v => setDaysAfter(v)}
                    onEnterPressed={onEnterPressed}
                    decimalPoints={0}
                  />
                  {RESX.Exclusivity.daysAfterShow}
                </div>
              </div>
            </LabelledRow>
          </tbody>
        </table>
      </ValidatorContext>
    </div>
    <div className="flex margin-top-bigger">
      <div className="grow1"></div>
      <Button
        primary
        loading={isSaving}
        disabled={isSaving}
        onClick={onSubmit}
      >
        {RESX.Buttons.btnSave}
      </Button>
    </div>
  </>
}